import { useState } from 'react';
import type { MapFilters, MapLayers } from './types';
import { EMPTY_FILTERS, countActiveFilters } from './types';
import type { FilterOptions } from './MapFiltersPanel';

interface Props {
  filter: MapFilters;
  onChange: (f: MapFilters) => void;
  search: string;
  onSearch: (v: string) => void;
  onOpenFilters: () => void;
  layers: MapLayers;
  onLayers: (l: MapLayers) => void;
  count: number;
  loading?: boolean;
  options: FilterOptions;
  right?: React.ReactNode;
}

const num = (v: string) => v.replace(/[^0-9]/g, '');

// Map layer toggles: label + legend-dot color (matches the on-map markers).
const LAYER_META: Record<keyof MapLayers, { label: string; dot: string }> = {
  properties: { label: 'Properties', dot: '#374151' },
  permits: { label: 'Permits', dot: '#f59e0b' },
  tenants: { label: 'Tenants', dot: '#0d9488' },
};
const titleCase = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const kfmt = (v: string) => {
  const n = Number(v);
  if (!n) return v;
  return n >= 1_000_000 ? +(n / 1_000_000).toFixed(1) + 'M' : n >= 1000 ? Math.round(n / 1000) + 'K' : String(n);
};
const rangeLabel = (base: string, min: string, max: string, pre = '', suf = '') => {
  if (min && max) return `${pre}${kfmt(min)}-${kfmt(max)}${suf}`;
  if (min) return `${pre}${kfmt(min)}+${suf}`;
  if (max) return `≤${pre}${kfmt(max)}${suf}`;
  return base;
};

export default function MapFilterBar({ filter, onChange, search, onSearch, onOpenFilters, layers, onLayers, count, loading, options, right }: Props) {
  const [open, setOpen] = useState<string | null>(null);
  const set = (k: keyof MapFilters, v: string) => onChange({ ...filter, [k]: v });
  const activeCount = countActiveFilters(filter);

  // CoStar-style dropdown button: shows the value (or label) + a chevron; an
  // active one gets a subtle highlight and an inline ✕ to clear.
  const dropdownButton = (id: string, key: keyof MapFilters, label: string, active: boolean, body: React.ReactNode) => (
    <div className="relative">
      <button onClick={() => setOpen(open === id ? null : id)}
        className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm transition-colors ${active ? 'border-primary-400 text-primary-500 bg-primary-50' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}`}>
        <span className="max-w-[140px] truncate">{label}</span>
        {active ? (
          <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); set(key, ''); }} className="text-gray-400 hover:text-gray-700">✕</span>
        ) : (
          <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
        )}
      </button>
      {open === id && (
        <div className="absolute z-[1200] mt-1 bg-white border rounded-lg shadow-lg p-3 min-w-[210px]" onMouseLeave={() => setOpen(null)}>
          {body}
        </div>
      )}
    </div>
  );

  const money = (k: keyof MapFilters, ph: string) => (
    <input inputMode="numeric" placeholder={ph} value={filter[k]}
      onChange={(e) => set(k, num(e.target.value))} className="w-full border rounded px-2 py-1 text-sm" />
  );

  const dropdown = (k: keyof MapFilters, allLabel: string, opts: string[]) => (
    <select value={filter[k]} onChange={(e) => { set(k, e.target.value); setOpen(null); }} className="w-full border rounded px-2 py-1 text-sm" size={Math.min(opts.length + 1, 10)}>
      <option value="">{allLabel}</option>
      {opts.map((o) => <option key={o} value={o}>{titleCase(o)}</option>)}
    </select>
  );

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Search (always) */}
      <div className="relative flex-1 min-w-[150px] md:max-w-sm">
        <svg className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
        <input
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Address, place, name"
          className="w-full border border-gray-300 rounded-full pl-8 pr-8 py-2 md:py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
        {search && <button onClick={() => onSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm">✕</button>}
      </div>

      {/* Quick dropdown buttons (desktop only) */}
      <div className="hidden md:flex items-center gap-2">
        {dropdownButton('type', 'type', filter.type ? titleCase(filter.type) : 'Property Type', !!filter.type, dropdown('type', 'All types', options.types))}
        {dropdownButton('submarket', 'submarket', filter.submarket || 'Submarket', !!filter.submarket, dropdown('submarket', 'All submarkets', options.submarkets))}
        {dropdownButton('price', 'min_price', rangeLabel('Price', filter.min_price, filter.max_price, '$'), !!(filter.min_price || filter.max_price),
          <div className="space-y-2">{money('min_price', 'Min $')}{money('max_price', 'Max $')}</div>)}
        {dropdownButton('size', 'min_building_size', rangeLabel('Size', filter.min_building_size, filter.max_building_size, '', ' SF'), !!(filter.min_building_size || filter.max_building_size),
          <div className="space-y-2">{money('min_building_size', 'Min SF')}{money('max_building_size', 'Max SF')}</div>)}
        {(activeCount > 0 || search) && (
          <button onClick={() => { onChange({ ...EMPTY_FILTERS }); onSearch(''); }} className="px-2 py-1.5 text-sm text-gray-500 hover:text-gray-800">Clear</button>
        )}
      </div>

      {/* Filters (always, CoStar-blue) */}
      <button onClick={onOpenFilters}
        className={`inline-flex items-center gap-1.5 px-3 py-2 md:py-1.5 text-sm rounded-md border transition-colors ${activeCount ? 'bg-primary-500 text-white border-primary-500 hover:bg-primary-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h18M6 8h12M9 12h6M11 16h2" /></svg>
        Filters{activeCount ? <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[11px] bg-white/25 text-white">{activeCount}</span> : null}
      </button>

      {/* Layers (desktop only) */}
      <span className="hidden md:inline-block mx-1 h-5 w-px bg-gray-200" />
      {/* Layer toggles as a quiet segmented control; the colored dot doubles as a
          legend (charcoal = property pins, amber = permit pins). Both can be on. */}
      <div className="hidden md:inline-flex items-center rounded-lg bg-gray-100 p-0.5">
        {(['properties', 'permits', 'tenants'] as const).map((k) => (
          <button key={k} onClick={() => onLayers({ ...layers, [k]: !layers[k] })}
            aria-pressed={layers[k]}
            title={`Toggle ${LAYER_META[k].label.toLowerCase()} pins`}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${layers[k] ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: LAYER_META[k].dot }} />
            {LAYER_META[k].label}
          </button>
        ))}
      </div>
      <span className="text-xs text-gray-500 ml-1 inline-flex items-center gap-1 whitespace-nowrap">
        {loading && <span className="w-3 h-3 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin" />}
        {loading ? '…' : `${count.toLocaleString()}`}<span className="hidden sm:inline"> results</span>
      </span>
      <div className="ml-auto hidden md:flex items-center gap-2">{right}</div>
    </div>
  );
}
